import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, withTypes } from 'react-final-form';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { createMuiTheme, makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import { Notification } from 'react-admin';
import { useTranslate, useLogin, useNotify } from 'ra-core';
import { lightTheme } from './themes';
import { Location } from 'history';
import logo from "../images/logo.png";
import * as apiClient from "../common/apiClient";
import { useHistory } from "react-router-dom";

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  '@global': {
    '@keyframes fadeSlideUp': {
      from: { opacity: 0, transform: 'translateY(24px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
    '@keyframes shimmer': {
      '0%':   { backgroundPosition: '-200% center' },
      '100%': { backgroundPosition: '200% center' },
    },
    '@keyframes floatDot': {
      '0%, 100%': { transform: 'translateY(0px)' },
      '50%':      { transform: 'translateY(-12px)' },
    },
  },

  // ── Page
  main: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      paddingTop: theme.spacing(4),
    },
  },

  // ── Decorative background blobs
  blob1: {
    position: 'absolute',
    width: 500,
    height: 500,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 70%)',
    top: '-120px',
    right: '-100px',
    pointerEvents: 'none',
  },
  blob2: {
    position: 'absolute',
    width: 400,
    height: 400,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)',
    bottom: '-80px',
    left: '-80px',
    pointerEvents: 'none',
  },
  gridLines: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
    `,
    backgroundSize: '60px 60px',
    pointerEvents: 'none',
  },

  // ── Card container (split layout)
  container: {
    display: 'flex',
    width: '100%',
    maxWidth: 860,
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)',
    animation: '$fadeSlideUp 0.6s cubic-bezier(0.16,1,0.3,1) both',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      maxWidth: 420,
    },
  },

  // ── Left panel (branding)
  brandPanel: {
    flex: '0 0 340px',
    background: 'linear-gradient(160deg, #0ea5e9 0%, #2563eb 50%, #4f46e5 100%)',
    padding: '48px 36px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flex: 'none',
      padding: '32px 28px',
    },
  },
  brandPanelGlow: {
    position: 'absolute',
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.08)',
    top: -80,
    right: -80,
    pointerEvents: 'none',
  },
  brandPanelGlow2: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)',
    bottom: -40,
    left: -40,
    pointerEvents: 'none',
  },
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    position: 'relative',
    zIndex: 1,
  },
  logoImg: {
    width: 48,
    height: 48,
    borderRadius: 12,
    objectFit: 'contain',
    background: 'rgba(255,255,255,0.95)',
    padding: 6,
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
  },
  logoText: {
    color: '#fff',
    fontWeight: 800,
    fontSize: '1.1rem',
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
    fontFamily: "'DM Sans', sans-serif",
  },
  logoSub: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.72rem',
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  brandBody: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '32px 0',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 0 0',
    },
  },
  brandHeadline: {
    color: '#fff',
    fontWeight: 800,
    fontSize: '1.75rem',
    lineHeight: 1.25,
    marginBottom: 12,
    fontFamily: "'DM Sans', sans-serif",
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.4rem',
    },
  },
  brandDesc: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.875rem',
    lineHeight: 1.65,
    fontFamily: "'DM Sans', sans-serif",
  },
  featureList: {
    marginTop: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: 'rgba(255,255,255,0.85)',
    fontSize: '0.82rem',
    fontFamily: "'DM Sans', sans-serif",
  },
  featureDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.6)',
    flexShrink: 0,
  },
  brandFooter: {
    position: 'relative',
    zIndex: 1,
    color: 'rgba(255,255,255,0.4)',
    fontSize: '0.72rem',
    fontFamily: "'DM Sans', sans-serif",
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  // ── Right panel (form)
  formPanel: {
    flex: 1,
    background: '#ffffff',
    padding: '48px 44px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '32px 28px',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '28px 20px',
    },
  },
  formHeader: {
    marginBottom: 32,
  },
  formTitle: {
    fontSize: '1.6rem',
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.02em',
    fontFamily: "'DM Sans', sans-serif",
    marginBottom: 6,
  },
  formSubtitle: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontFamily: "'DM Sans', sans-serif",
  },

  // ── Inputs
  inputWrap: {
    marginBottom: 18,
  },
  inputLabel: {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#374151',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: 6,
    fontFamily: "'DM Sans', sans-serif",
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      fontSize: '0.92rem',
      fontFamily: "'DM Sans', sans-serif",
      background: '#f8fafc',
      transition: 'all 0.2s ease',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb',
      },
      '&.Mui-focused': {
        background: '#fff',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#2563eb',
          borderWidth: 2,
        },
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e2e8f0',
    },
  },
  forgotWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: -6,
    marginBottom: 10,
  },
  forgotLink: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 700,
    color: '#2563eb',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'color 0.2s ease',
    '&:hover': {
      color: '#1d4ed8',
      textDecoration: 'underline',
    },
  },

  // ── Submit button
  submitBtn: {
    width: '100%',
    height: 50,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.95rem',
    fontFamily: "'DM Sans', sans-serif",
    letterSpacing: '0.02em',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginTop: 8,
    boxShadow: '0 4px 16px rgba(37,99,235,0.35)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 6px 24px rgba(37,99,235,0.45)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&:disabled': {
      opacity: 0.7,
      cursor: 'not-allowed',
      transform: 'none',
    },
  },

  // ── Divider
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '20px 0',
    '& span': {
      height: 1,
      flex: 1,
      background: '#e2e8f0',
    },
    '& p': {
      fontSize: '0.75rem',
      color: '#94a3b8',
      fontFamily: "'DM Sans', sans-serif",
      whiteSpace: 'nowrap',
      margin: 0,
    },
  },

  // ── Footer note
  formFooter: {
    marginTop: 20,
    textAlign: 'center',
    fontSize: '0.78rem',
    color: '#94a3b8',
    fontFamily: "'DM Sans', sans-serif",
  },
  forgotDialogTitle: {
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 800,
    color: '#0f172a',
  },
  forgotDialogText: {
    margin: '4px 0 14px',
    color: '#64748b',
    fontSize: '0.86rem',
    fontFamily: "'DM Sans', sans-serif",
    lineHeight: 1.5,
  },
  forgotDialogNote: {
    marginTop: 10,
    color: '#b45309',
    fontSize: '0.8rem',
    fontFamily: "'DM Sans', sans-serif",
    lineHeight: 1.45,
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 8,
    padding: '8px 10px',
  },
  forgotDialogInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: '#f8fafc',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb',
      },
    },
  },
  forgotCancelBtn: {
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#334155',
    borderRadius: 8,
    minWidth: 92,
    height: 38,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
  },
  forgotSubmitBtn: {
    border: 'none',
    background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
    color: '#fff',
    borderRadius: 8,
    minWidth: 120,
    height: 38,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    '&:disabled': {
      opacity: 0.7,
      cursor: 'not-allowed',
    },
  },
}));

// ─── Types & helpers ──────────────────────────────────────────────────────────

interface FormValues { email?: string; password?: string; }
const { Form } = withTypes<FormValues>();

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    variant="outlined"
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

// ─── Component ────────────────────────────────────────────────────────────────

const Login = ({ location }: { location: Location }) => {
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUserName, setForgotUserName] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const translate = useTranslate();
  const classes = useStyles({});
  const notify = useNotify();
  const login = useLogin();
  const history = useHistory();

  const handleForgotPassword = async () => {
    if (!forgotUserName.trim()) {
      notify('Please enter your username / email', 'warning');
      return;
    }

    setForgotSubmitting(true);
    try {
      const response = await fetch(apiClient.APIUrl + '/users/forgot_password_request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userName: forgotUserName.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        notify(data.status || 'If the user exists, password reset instructions have been sent', 'info');
        setForgotOpen(false);
        setForgotUserName('');
      } else {
        notify(data.status || 'Failed to initiate password reset', 'warning');
      }
    } catch (err) {
      notify('An error occurred while initiating password reset', 'warning');
    } finally {
      setForgotSubmitting(false);
    }
  };

  const handleSubmit = (auth: FormValues) => {
    setLoading(true);
    const formdata = { username: auth.email, password: auth.password, usertype: 'admin' };

    apiClient.loginUser(formdata).then(res => {
      setLoading(false);
      if (res[0].result === true) {
        const user = res[0];
        localStorage.setItem("firstname",    user.UserName);
        localStorage.setItem("lastname",     "");
        localStorage.setItem('username',     user.UserName);
        localStorage.setItem("userId",       user.ID);
        localStorage.setItem("type",         user.RoleTypeId===1?"isAdmin":"isCustLogin");
        localStorage.setItem("displayName",  user.UserName);
        localStorage.setItem("TypeID",       user.TypeID);
        localStorage.setItem("RoleTypeId",   user.RoleTypeId);
        localStorage.setItem("EmployeeId",   user.EmployeeId);
        localStorage.setItem("ApproverId",   user.ApproverId);
        localStorage.setItem("token",        user.token);

        apiClient.getmodules(user.ID).then(res => {
          let resArr = [...res];
          resArr.forEach(element => {
            if (element.ModuleRights !== undefined) {
              const mrArr = element.ModuleRights.split(",");
              mrArr.forEach((r: string) => {
                const rArr = r.split("|");
                element[rArr[1]] = (parseInt(rArr[2])) ? true : false;
              });
              element["hasList"]  = element["hasView"];
              element["hasShow"]  = element["hasView"];
              element["View"]     = element["hasView"]   === true ? 1 : 0;
              element["Edit"]     = element["hasEdit"]   === true ? 1 : 0;
              element["Delete"]   = element["hasDelete"] === true ? 1 : 0;
              element["Create"]   = element["hasCreate"] === true ? 1 : 0;
              element["Export"]   = element["hasExport"] === true ? 1 : 0;
            }
          });
          localStorage.setItem("menu", JSON.stringify(resArr));
          if (user.isFirstLogin)         history.push("/changepassword");
          else 
            {
               history.push(user.DefaultScreen);
            }
          window.location.reload();
        }).catch(err => console.log(err));
      } else {
        notify(res[0].message, 'warning');
      }
    }).catch(err => {
      setLoading(false);
      notify(err, 'warning');
    });
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.email)    errors.email    = translate('ra.validation.required');
    if (!values.password) errors.password = translate('ra.validation.required');
    return errors;
  };

  const features = [
    'Employee & attendance management',
    'Leave & salary processing',
    'Reports & analytics dashboard',
    'Role-based access control',
  ];

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validate}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} noValidate>
          <div className={classes.main}>
            {/* Background decorations */}
            <div className={classes.gridLines} />
            <div className={classes.blob1} />
            <div className={classes.blob2} />

            <div className={classes.container}>

              {/* ── Left brand panel ── */}
              <div className={classes.brandPanel}>
                <div className={classes.brandPanelGlow} />
                <div className={classes.brandPanelGlow2} />

                {/* Logo */}
                <div className={classes.logoWrap}>
                  <img src={logo} alt="Logo" className={classes.logoImg} />
                  <div>
                    <div className={classes.logoText}>Admin Portal</div>
                    <div className={classes.logoSub}>Management System</div>
                  </div>
                </div>

                {/* Headline */}
                <div className={classes.brandBody}>
                  <div className={classes.brandHeadline}>
                    Everything you need to manage your organization
                  </div>
                  <div className={classes.brandDesc}>
                    Secure, fast, and intuitive tools for HR and operations teams.
                  </div>
                  <div className={classes.featureList}>
                    {features.map((f, i) => (
                      <div key={i} className={classes.featureItem}>
                        <div className={classes.featureDot} />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>

                <div className={classes.brandFooter}>
                  © {new Date().getFullYear()} All rights reserved
                </div>
              </div>

              {/* ── Right form panel ── */}
              <div className={classes.formPanel}>
                <div className={classes.formHeader}>
                  <div className={classes.formTitle}>Sign in</div>
                  <div className={classes.formSubtitle}>Enter your credentials to access the dashboard</div>
                </div>

                {/* Email */}
                <div className={classes.inputWrap}>
                  <label className={classes.inputLabel}>Email / Mobile</label>
                  <Field
                    autoFocus
                    name="email"
                    component={renderInput as any}
                    placeholder="you@example.com"
                    disabled={loading}
                    className={classes.textField}
                  />
                </div>

                {/* Password */}
                <div className={classes.inputWrap}>
                  <label className={classes.inputLabel}>Password</label>
                  <Field
                    name="password"
                    component={renderInput as any}
                    placeholder="••••••••"
                    type="password"
                    disabled={loading}
                    className={classes.textField}
                  />
                </div>

                <div className={classes.forgotWrap}>
                  <button
                    type="button"
                    className={classes.forgotLink}
                    onClick={() => setForgotOpen(true)}
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className={classes.submitBtn}
                  disabled={loading}
                >
                  {loading
                    ? <CircularProgress size={20} style={{ color: '#fff' }} />
                    : null}
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>

                <div className={classes.formFooter}>
                  Protected by secure authentication &nbsp;·&nbsp; Admin access only
                </div>
              </div>
            </div>

            <Notification />

            <Dialog
              open={forgotOpen}
              onClose={() => {
                if (!forgotSubmitting) {
                  setForgotOpen(false);
                }
              }}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle className={classes.forgotDialogTitle}>Forgot Password</DialogTitle>
              <DialogContent>
                <p className={classes.forgotDialogText}>
                  Enter your username or email address and we will send you a reset link.
                </p>
                <TextField
                  variant="outlined"
                  label="Username / Email"
                  placeholder="you@example.com"
                  value={forgotUserName}
                  onChange={(e) => setForgotUserName(e.target.value)}
                  fullWidth
                  autoFocus
                  disabled={forgotSubmitting}
                  className={classes.forgotDialogInput}
                />
                <div className={classes.forgotDialogNote}>
                  Note: Forgot password works only with Email ID. Mobile number is not supported.
                </div>
              </DialogContent>
              <DialogActions style={{ padding: '8px 24px 20px' }}>
                <button
                  type="button"
                  className={classes.forgotCancelBtn}
                  disabled={forgotSubmitting}
                  onClick={() => {
                    setForgotOpen(false);
                    setForgotUserName('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={classes.forgotSubmitBtn}
                  disabled={forgotSubmitting}
                  onClick={handleForgotPassword}
                >
                  {forgotSubmitting ? <CircularProgress size={16} style={{ color: '#fff' }} /> : null}
                  {forgotSubmitting ? 'Sending...' : 'Send reset link'}
                </button>
              </DialogActions>
            </Dialog>
          </div>
        </form>
      )}
    />
  );
};

Login.propTypes = {
  authProvider: PropTypes.func,
  previousRoute: PropTypes.string,
};

const LoginWithTheme = (props: any) => (
  <ThemeProvider theme={createMuiTheme(lightTheme)}>
    <Login {...props} />
  </ThemeProvider>
);

export default LoginWithTheme;